<template>
  <div class="flex flex-col w-full h-full min-w-full">
    <div class="flex flex-row justify-between">
      <div class="my-auto text-sm font-semibold uppercase" v-text="t('general.filter')" />
      <div class="flex items-center">
        <tx-button type="text" class="text-primary-500" :text="t('general.clear')" @click="doReset" />
      </div>
    </div>
    <div
      v-if="includeSearch"
      class="relative p-4 mb-2 bg-yellow-100"
    >
      <span>{{ t('assetsList.narrowDownSearch') }}</span>
    </div>
    <div
      class="relative p-4 mb-2 bg-gray-50"
    >
      <fieldset>
        <label class="w-full text-sm tracking-wide uppercase">
          {{ t('general.narrowDown') }}</label>
        <tx-input
          v-model.trim="narrowDown"
          type="textarea" class="mb-3" clearable
        />
      </fieldset>
    </div>
    <div class="flex-shrink mb-5 overflow-auto grow" style="min-width: 0;">
      <fieldset
        v-for="itm in filteredCriteria" :key="itm.prop" class="relative p-3 mb-2 ml-2 "
      >
        <label class="w-full text-sm tracking-wide uppercase" v-text="itm.label" />
        <tx-select
          v-if="itm.type === 'vetting'" v-model="itm.value" filterable multiple-values clearable
          :data="itm.vetting"
        />
        <tx-input
          v-else-if="itm.type === 'string'" v-model.trim="itm.value as string | null"
          type="textarea" class="mb-3" clearable
        />
        <tx-range
          v-else-if="itm.type === 'decimal'" v-model:from="itm.rangeValue!.from" v-model:to="itm.rangeValue!.to" step="0.01"
          class="mb-3" clearable
        />
      </fieldset>
    </div>
    <div class="flex flex-row justify-between flex-shrink-0 mb-5 h-14 min-h-14">
      <tx-button type="cancel" :text="t('general.cancel')" @click="doCancel" />
      <tx-button type="confirm" :text="t('general.apply')" @click="doConfirm" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import TxButton from '@/shared/components/TxButton.vue'
import TxSelect from '@/shared/components/TxSelect.vue'
import TxInput from '@/shared/components/TxInput.vue'
import TxRange from '@/shared/components/TxRange.vue'
import { useUserStore } from '@/store/userData'

interface AssetsFilterCriteria {
  prop: string
  label: string
  type: 'vetting' | 'string' | 'decimal'
  vetting?: string[]
  value: string | string[] | null
  rangeValue?: { from: number | null, to: number | null } | null
}

const props = defineProps<{
  modelValue: AssetsFilterCriteria[]
  searchText?: string
}>()

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'confirm', criteriaString: string, criteriaForDisplay: string[], includeSearch: boolean): void
  (e: 'update:modelValue', modelValue: AssetsFilterCriteria[]): void
}>()

const { t } = useI18n()
const narrowDown = ref('')
const includeSearch = ref(false)

onMounted(() => {
  if (props.searchText && props.searchText.length > 0 && props.searchText.trim() !== '*') {
    includeSearch.value = true
  }
})

const localCriteria = computed({
  get: () => props.modelValue,
  set: modelValue => emit('update:modelValue', modelValue),
})

const filteredCriteria = computed(() => {
  return localCriteria.value.filter((itm) => {
    if (narrowDown.value) {
      const lowerNarrowDown = narrowDown.value.toLowerCase()
      return itm.label.toLowerCase().includes(lowerNarrowDown)
    }
    return true
  })
})
const userStore = useUserStore()

const defaultCriteria = ref<{ prop: string, label: string, type: 'vetting' | 'string' | 'decimal', vetting: string[] | undefined, value: string | string[] | null, rangeValue?: { from: number | null, to: number | null } | null }[]>([
  { prop: 'ColorFamily', label: 'Color Family', type: 'vetting', vetting: ['Blacks', 'Med Indigo - Worn In', 'Dark Indigo - Worn In', 'Light Indigo - Worn In', 'Dark Indigo - Flat Finish', 'Neutrals', 'Light Indigo - Flat Finish', 'Med Indigo - Flat Finish', 'Whites', 'Greens', 'Greys', 'Browns', 'Blues'], value: null },
  { prop: 'MaterialCode', label: 'Material Code', type: 'string', vetting: undefined, value: null },
  { prop: 'PrimaryFabric', label: 'Primary Fabric', type: 'string', vetting: undefined, value: null },
  { prop: 'MaterialType', label: 'Material Type', type: 'vetting', vetting: ['Woven - Denim', 'Woven - Non Denim'], value: null },
  { prop: 'GenericContent', label: 'Generic Content', type: 'string', vetting: undefined, value: null },
  { prop: 'BrandedContent', label: 'Branded Content', type: 'string', vetting: undefined, value: null },
  { prop: 'FabricMill', label: 'Fabric Mill', type: 'string', vetting: undefined, value: null },
  { prop: 'FabricAfterWashWeight', label: 'Fabric After Wash Weight', type: 'string', vetting: undefined, value: null },
  { prop: 'ProductPricePositioning', label: 'Product Price Positioning', type: 'vetting', vetting: ['TIER 2', 'TIER 3'], value: null },
  { prop: 'FabricStretchPerc', label: 'Fabric Stretch %', type: 'string', vetting: undefined, value: null },
  { prop: 'StretchBucket', label: 'Stretch Bucket', type: 'vetting', vetting: ['0% NO STRETCH', '46%-60%', '36%-45%', '21%-35%', '1%-20%', '61%-80%'], value: null },
  { prop: 'Denim', label: 'Denim', type: 'vetting', vetting: ['Denim', 'Non-Denim'], value: null },
  { prop: 'TargetFOB', label: 'Target FOB', type: 'decimal', vetting: undefined, value: null, rangeValue: { from: null, to: null } },
  { prop: 'Performance', label: 'Performance', type: 'vetting', vetting: ['Cool', 'Warm', 'Not Applicable', 'Levi\'s Flex'], value: null },
  { prop: 'Selvedge', label: 'Selvedge', type: 'vetting', vetting: ['No', 'Yes'], value: null },
  { prop: 'StretchType', label: 'Stretch Type', type: 'vetting', vetting: ['Non-Stretch', 'Stretch'], value: null },
  { prop: 'Destruction', label: 'Destruction', type: 'vetting', vetting: ['None', 'Minimal', 'Overt'], value: null },
  { prop: 'ExtendedSizes', label: 'Extended Sizes', type: 'string', vetting: undefined, value: null },
  { prop: 'HemTreatment', label: 'Hem Treatment', type: 'vetting', vetting: ['Finished', 'Clean Hem with Damage', 'Cut Hem with Damage', 'Cut-Off', 'Cut Off BW', 'Rolled'], value: null },
  { prop: 'FabricStory', label: 'Fabric Story', type: 'string', vetting: undefined, value: null },
  { prop: 'FinishType', label: 'Finish Type', type: 'vetting', vetting: ['Worn In', 'Stonewash', 'Destructed', 'Rinse', 'Acid Wash', 'Rigid'], value: null },
  { prop: 'PrimaryFinish', label: 'Primary Finish', type: 'string', vetting: undefined, value: null },
  { prop: 'ShadeColor', label: 'Shade Color', type: 'vetting', vetting: ['Dark', 'Medium', 'Light', 'S Light', 'Saturated'], value: null },
])
// Get criteria based on config
const sourceCriteria = computed((): AssetsFilterCriteria[] => {
  if (userStore.activeCatalog?.Config?.DigitalAssetFilterCriteria?.length) {
    return userStore.activeCatalog.Config.DigitalAssetFilterCriteria as AssetsFilterCriteria[]
  }
  else if (userStore.sellerConfig?.DigitalAssetFilterCriteria?.length) {
    return userStore.sellerConfig.DigitalAssetFilterCriteria as AssetsFilterCriteria[]
  }
  else {
    return defaultCriteria.value
  }
})

function doReset() {
  localCriteria.value = localCriteria.value.map((itm) => {
    const newItm = { ...itm }
    newItm.value = null
    if (newItm.rangeValue) {
      newItm.rangeValue.from = null
      newItm.rangeValue.to = null
    }
    return newItm
  })
  includeSearch.value = false
}

function doCancel() {
  emit('cancel')
}

function doConfirm() {
  const criteriaString = localCriteria.value.map((itm) => {
    if (itm.type === 'vetting' && itm.value) {
      return `search.in(Metadata/${itm.prop}, '${(Array.isArray(itm.value) ? itm.value.join('|') : itm.value).replace('\'', '\'\'')}', '|')`
    }
    else if (itm.type === 'string' && itm.value) {
      return `search.ismatch('${(itm.value as string).replace('\'', '\'\'')}', 'Metadata/${itm.prop}')`
    }
    else if (itm.type === 'decimal' && itm.rangeValue) {
      const res: string[] = []
      if (itm.rangeValue.from !== null) {
        res.push(`Metadata/${itm.prop} ge ${itm.rangeValue.from}`)
      }
      if (itm.rangeValue.to !== null) {
        res.push(`Metadata/${itm.prop} le ${itm.rangeValue.to}`)
      }
      if (res.length > 0) {
        return res.join(' and ')
      }
    }
    return ''
  }).filter(itm => itm).join(' and ')

  const criteriaForDisplay = localCriteria.value.map((itm) => {
    if (itm.type === 'vetting' && itm.value) {
      return `${itm.label}: ${Array.isArray(itm.value) ? itm.value.join(', ') : itm.value}`
    }
    else if (itm.type === 'string' && itm.value) {
      return `${itm.label}: ${itm.value}`
    }
    else if (itm.type === 'decimal' && itm.rangeValue && (itm.rangeValue.from !== null || itm.rangeValue.to !== null)) {
      const rangeParts: string[] = []
      if (itm.rangeValue.from !== null) {
        rangeParts.push(`from ${itm.rangeValue.from}`)
      }
      if (itm.rangeValue.to !== null) {
        rangeParts.push(`to ${itm.rangeValue.to}`)
      }
      return `${itm.label}: ${rangeParts.join(' - ')}`
    }
    return ''
  }).filter(itm => itm.length > 0)

  emit('confirm', criteriaString, criteriaForDisplay, includeSearch.value)
}

watch(sourceCriteria, (newSourceCriteria) => {
  const initializedCriteria = newSourceCriteria.map(c => ({
    ...c,
    value: null,
    rangeValue: c.type === 'decimal' ? { from: null, to: null } : undefined,
  }))
  localCriteria.value = initializedCriteria
}, { immediate: true })
</script>
